import {
  buildAdminGeoPreviewUrl,
  buildUsExclusiveVariantPreviewUrl,
} from "@/lib/adminGeoPreview";
import { usesSpanishDefaultForSite } from "@/lib/siteGeoPolicy";

/** Canonical membership funnel path for Spanish / Argentina visitors. */
export const MEMBERSHIP_LANDING_PATH_ES = "/ser-miembro";

/** Canonical membership funnel path for English / international visitors. */
export const MEMBERSHIP_LANDING_PATH_INTL = "/membership";

/**
 * Resolves the locale-appropriate membership landing path for the current visitor.
 * AR + Spanish-default geo → `/ser-miembro`; everyone else → `/membership`.
 */
export function getMembershipLandingPath(): string {
  if (typeof window === "undefined") return MEMBERSHIP_LANDING_PATH_ES;
  return usesSpanishDefaultForSite()
    ? MEMBERSHIP_LANDING_PATH_ES
    : MEMBERSHIP_LANDING_PATH_INTL;
}

/** Static CTA path for country landings (config has no runtime geo). */
export function membershipLandingPathForLocale(locale: string): string {
  const base = locale.toLowerCase().split("-")[0];
  return base === "es" ? MEMBERSHIP_LANDING_PATH_ES : MEMBERSHIP_LANDING_PATH_INTL;
}

/** Canonical fullscreen membership path from ISO2 (US → intl/Stripe EN, resto → ES/Stripe). */
export function membershipLandingPathForCountry(
  country: string | null | undefined,
): string {
  const code = String(country ?? "")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  return code === "US"
    ? MEMBERSHIP_LANDING_PATH_INTL
    : MEMBERSHIP_LANDING_PATH_ES;
}

export type MembershipPreviewGateway = "ar" | "intl";

/** Fullscreen membership path per market (AR → MP, intl → Stripe). */
export function membershipFullscreenPathForGateway(
  gateway: MembershipPreviewGateway,
): string {
  return gateway === "ar"
    ? MEMBERSHIP_LANDING_PATH_ES
    : MEMBERSHIP_LANDING_PATH_INTL;
}

/**
 * Admin preview URL with ?_force_variant= (skips geo redirect in MembershipLandingRoute).
 */
export function buildMembershipVariantPreviewUrl(
  variantId: string,
  options?: {
    gateway?: MembershipPreviewGateway;
    /** fullscreen → /membership|/ser-miembro · home → / con template forzado */
    surface?: "fullscreen" | "home" | "us-exclusive";
    /** Solo surface=us-exclusive: simular state_us en el worker (ej. FL). */
    usState?: string;
    /** ?lang= para i18n en preview admin. */
    lang?: "es" | "en";
    /** Simula cf.country en el worker (Stripe intl / MP AR desde admin en otro país). */
    simulateCountry?: string;
    simulateState?: string;
    simulateProvince?: string;
    simulateCity?: string;
    /** false = solo ?_force_variant (legacy). Default true en admin. */
    simulateWorkerGeo?: boolean;
    /** Simulate real VPN visitor effects (hideCta, privacy flags, etc) in admin preview. */
    simulateVpn?: boolean;
    /** Use real IPLocate data for this IP in simulation (privacy, geo). See simulate_ip param. */
    simulateIp?: string;
    /** Simulate real VPN (for admin QA previews to show real visitor incl. hideCta/privacy effects). */
    simulateVpn?: boolean;
  },
): string {
  const slug = variantId.trim();
  const gateway = options?.gateway ?? "intl";
  const surface = options?.surface ?? "fullscreen";
  const simulateWorkerGeo = options?.simulateWorkerGeo !== false;
  const defaultCountry = gateway === "ar" ? "AR" : "US";
  const country = (options?.simulateCountry ?? defaultCountry).trim().toUpperCase();
  // Default admin previews to simulate real VPN (see user request: "if is admin must need simulate real a vpn")
  const effectiveSimulateVpn = options?.simulateVpn !== false;

  if (surface === "us-exclusive") {
    return buildUsExclusiveVariantPreviewUrl(slug, {
      state: options?.usState ?? options?.simulateState,
      city: options?.simulateCity,
      simulateWorkerGeo,
      lang: options?.lang,
      simulateIp: options?.simulateIp,
    });
  }
  if (surface === "home") {
    return buildAdminGeoPreviewUrl({
      path: "/",
      country: simulateWorkerGeo ? country : undefined,
      state: options?.simulateState,
      province: options?.simulateProvince,
      city: options?.simulateCity,
      forceVariant: slug,
      previewTemplate: "video",
      includeDebug: simulateWorkerGeo,
      lang: options?.lang,
      simulateVpn: options?.simulateVpn,
      simulateIp: options?.simulateIp,
    });
  }
  const path = membershipFullscreenPathForGateway(gateway);
  if (!simulateWorkerGeo && !options?.simulateCountry) {
    const params = new URLSearchParams({
      _force_variant: slug,
      _admin_preview: "1",
    });
    if (options?.lang) params.set("lang", options.lang);
    // still pass simulation intent via the simple path? (the full builder is preferred)
    if (effectiveSimulateVpn) params.set("simulate_vpn", "anonymous");
    if (options?.simulateIp?.trim()) params.set("simulate_ip", options.simulateIp.trim());
    return `${path}?${params.toString()}`;
  }
  return buildAdminGeoPreviewUrl({
    path,
    country,
    state: options?.simulateState,
    province: options?.simulateProvince,
    city: options?.simulateCity,
    forceVariant: slug,
    includeDebug: simulateWorkerGeo,
    lang: options?.lang,
    simulateVpn: effectiveSimulateVpn,
    simulateIp: options?.simulateIp,
  });
}

/**
 * Preview /membership como visitor de un estado US (cascada state_us → country:US).
 * Sin `_force_variant` — muestra la variante que el worker resolvería en producción.
 */
export function buildMembershipUsStatePreviewUrl(
  stateCode: string,
  options?: { lang?: "es" | "en" },
): string {
  const state = stateCode.trim().toUpperCase();
  return buildMembershipUsCityPreviewUrl("", { state, lang: options?.lang });
}

/**
 * Preview /membership como visitor US con cf.city (cascada city → state_us → country).
 * Pasá `city` vacío para simular solo por estado.
 */
export function buildMembershipUsCityPreviewUrl(
  city: string,
  options?: { state?: string; lang?: "es" | "en"; forceVariant?: string; simulateIp?: string },
): string {
  const cityTrim = city.trim();
  return buildAdminGeoPreviewUrl({
    path: MEMBERSHIP_LANDING_PATH_INTL,
    country: "US",
    state: options?.state?.trim().toUpperCase(),
    city: cityTrim || undefined,
    forceVariant: options?.forceVariant,
    includeDebug: true,
    lang: options?.lang,
    simulateVpn: true,
    simulateIp: options?.simulateIp,
  });
}